/**
 * Skills (ui-simplify §4.5, mockup docs/art/mockups/skills.jpg): V4 skill screen for the MAIN
 * hero. Left: the list (Rotation = bound abilities, read-only; Talents = nodes by tier). Centre:
 * the live VRM on the studio stage and the skill ring. Right: the info panel. Talent edits apply
 * to the active loadout at once (learnNode / unlearnNode); there is no draft, Save or loadout
 * switcher. Route params: `tab` ('rotation' | 'talents'), `nodeId` (preselect a talent).
 */
import type { ClassDef, ClassId } from '@keystone/sim';
import type { TalentId } from '@keystone/progression';
import { gridPoints, isCapstone, talentNode, withSpec } from '@keystone/progression';
import { ROUTES } from '../router'; // +grid
import { el } from '../../hud/dom';
import { iconUrl as abilityIcon, talentIconUrl } from '../../hud/icons';
import type { MetaScreenFactory } from '../context';
import '../kmmo/kmmo.css';
import {
  type HeroStage,
  type IconSpec,
  RESOURCE_ICON,
  classCrest,
  classEmblemSvg,
  divider,
  iconTabs,
  iconUrl,
  kmModal,
  kmToast,
  mountHeroStage,
  panel,
  primaryButton,
  roundIcon,
  screenFrame,
  secondaryButton,
  skillRing,
} from '../kmmo';
import { listGroupHeader, skillRow } from '../kmmo/extra-talents';
import { type TalentEdit, activeNodes, applyRecommended, describeNode, learnNode, nodeView, resetTalents, talentGrid, talentInfo, unlearnNode, lockReasonText } from './talentsModel';
import { abilityInfo, applyNodes, ringPicks, rotationRows, rowAbility, talentGroups, talentsByAbility, type RotationRow } from './skillsModel';
import './skills.css';

type Tab = 'rotation' | 'talents';

const abilityIconSpec = (id: string): IconSpec => abilityIcon(id, 64) ?? 'skills';
const talentIconSpec = (id: TalentId): IconSpec => talentIconUrl(id, 64) ?? 'skills';

export const skillsScreen: MetaScreenFactory = (ctx) => {
  const store = ctx.store;
  const classId = (): ClassId => store.profile.mainClassId;
  // +grid: talents are retired from live play; the second tab opens the Waystone Grid instead.
  let tab: Tab = 'rotation';
  let selAbility: string | null = null;
  let keepScroll = 0;
  let lastTab: Tab | null = null;
  let selNode: TalentId | null = typeof ctx.params['nodeId'] === 'string' ? (ctx.params['nodeId'] as TalentId) : null;

  const p0 = store.profile;
  const f = screenFrame({
    title: 'Skills',
    onBack: () => ctx.router.back(),
    onInfo: showInfo,
    currencies: [
      { id: 'gold', icon: 'gold', value: p0.currencies.gold ?? 0, title: 'Gold' },
      { id: 'fangsteel', icon: iconUrl(RESOURCE_ICON['fangsteel']!, true), value: p0.wallet.fangsteel ?? 0, title: 'Fangsteel' },
      { id: 'sigils', icon: iconUrl(RESOURCE_ICON['sigils']!, true), value: p0.wallet.sigils ?? 0, title: 'Altar Sigil' },
    ],
    className: 'ksk',
  });

  // Hero: the live VRM on the studio stage (a crest during a run: the GPU belongs to the run).
  let stage: HeroStage | null = null;
  let stageClass = classId();
  const mountStage = (): void => {
    stage?.dispose();
    stage = null;
    f.backdrop.replaceChildren();
    if (ctx.router.isOverlay) {
      const c = el('div', 'ksk-crest', f.backdrop);
      classCrest(stageClass, { size: 'lg' }, c);
      return;
    }
    try {
      stage = mountHeroStage(f.backdrop, { heroes: [stageClass], centerX: 0.45, fill: 0.74, feetY: 0.9, turn: 0.25, ring: true, interactive: true });
    } catch {
      classCrest(stageClass, { size: 'lg' }, el('div', 'ksk-crest', f.backdrop));
    }
  };
  mountStage();

  // Layout
  const tabsHost = el('div', 'ksk-tabs', f.body);
  const tabs = iconTabs(
    [
      { id: 'rotation', label: 'Rotation' },
      { id: 'talents', label: 'Waystone Grid' }, // +grid
    ],
    {
      active: tab,
      style: 'text',
      onChange: (id) => {
        if (id === 'talents') {
          tabs.set('rotation');
          ctx.router.open(ROUTES.grid); // +grid
          return;
        }
        tab = id as Tab;
        render();
      },
    },
    tabsHost,
  );
  const listPanel = panel({ className: 'ksk-list' }, f.body);
  const actions = el('div', 'ksk-actions', f.body);
  const ringHost = el('div', 'ksk-ring', f.body);
  const info = panel({ className: 'ksk-info', tone: 'solid' }, f.body);

  /** Validate locally (instant error text), then commit through the intent table (server-side online). */
  function apply(r: TalentEdit, ok: string, intent: () => Promise<{ ok: boolean; reason?: string; removed?: string[] }>): void {
    if (!r.ok) return void kmToast(r.reason, 'error');
    void intent().then((x) => {
      if (!x.ok) return void kmToast(x.reason ?? 'Could not change talents', 'error');
      const also = (x.removed ?? []).map((id) => talentNode(id)?.name ?? id);
      kmToast(also.length ? `${ok} (also unlearned ${also.join(', ')})` : ok, 'success');
    });
  }

  function showInfo(): void {
    const m = kmModal('Skills');
    el('p', 'km-modal-text', m.body, 'Rotation lists your abilities and their keys. Talents change them.');
    el('p', 'km-modal-text', m.body, 'You earn 1 talent point per hero level. Learning and unlearning is free and applies at once. Higher tiers open with points spent and with rank.');
    primaryButton('OK', { onClick: () => m.close() }, m.footer);
  }

  // -------------------------------------------------------------------------

  function render(): void {
    const p = store.profile;
    if (classId() !== stageClass) {
      stageClass = classId();
      if (stage) void stage.setHeroes([stageClass]);
      else mountStage();
    }
    f.currencies?.set('gold', p.currencies.gold ?? 0);
    f.currencies?.set('fangsteel', p.wallet.fangsteel ?? 0);
    f.currencies?.set('sigils', p.wallet.sigils ?? 0);

    // +specs: the skills list shows the chosen specialisation's evolved kit.
    const baseDef = ctx.content.class(classId());
    const def = baseDef ? withSpec(p, baseDef) : baseDef;
    const nodes = activeNodes(p, classId());
    const ti = talentInfo(p, classId());
    const grid = talentGrid(classId(), nodes, ti.points, ti.maxTier);
    // Talents tab dot: unspent points.
    const tb = tabs.el.querySelectorAll('.km-tab')[1] as HTMLElement | undefined;
    tb?.querySelector('.km-dot')?.remove();
    if (tb && gridPoints(p).free > 0) el('span', 'km-dot', tb); // +grid: unspent grid points
    tabs.set(tab);

    const prevScroll = listPanel.body.querySelector<HTMLElement>('.ksk-scroll');
    keepScroll = prevScroll && lastTab === tab ? prevScroll.scrollTop : 0;
    lastTab = tab;
    listPanel.body.replaceChildren();
    actions.replaceChildren();
    ringHost.replaceChildren();
    info.body.replaceChildren();
    f.el.dataset['tab'] = tab;
    if (!def) {
      el('div', 'ksk-empty', listPanel.body, 'No class data.');
      return;
    }
    if (tab === 'rotation') renderRotation(def, nodes);
    else renderTalents(def, nodes, grid);
  }

  // ---- Rotation ------------------------------------------------------------

  function renderRotation(base: ClassDef, nodes: TalentId[]): void {
    const rows = rotationRows(base, nodes);
    const usable = rows.filter((r) => !r.lockedBy);
    if (!rows.some((r) => r.abilityId === selAbility)) selAbility = usable[0]?.abilityId ?? null;
    const def = applyNodes(base, nodes);
    const scroll = el('div', 'ksk-scroll', listPanel.body);
    for (const r of rows) {
      const a = rowAbility(base, nodes, r);
      const facts = a ? abilityInfo(a, def, ctx.content) : null;
      skillRow({
        icon: abilityIconSpec(r.abilityId),
        name: r.name,
        sub: r.lockedBy ? `Talent: ${r.lockedBy.name}` : (facts?.cost || facts?.facts || ''),
        subTone: r.lockedBy ? 'muted' : 'blue',
        keyCap: r.key ?? undefined,
        locked: !!r.lockedBy,
        selected: r.abilityId === selAbility,
        onClick: () => { selAbility = r.abilityId; render(); },
      }, scroll);
    }
    scrollSelectedIntoView(scroll);

    const sel = rows.find((r) => r.abilityId === selAbility);
    if (sel) {
      const around = usable.filter((r) => r.abilityId !== sel.abilityId).slice(0, 6);
      skillRing(abilityIconSpec(sel.abilityId), around.map((r) => ({ icon: abilityIconSpec(r.abilityId), title: r.name, onClick: () => { selAbility = r.abilityId; render(); } })), {}, ringHost);
      abilityPanel(base, nodes, sel);
    }
  }

  function abilityPanel(base: ClassDef, nodes: TalentId[], row: RotationRow): void {
    const a = rowAbility(base, nodes, row);
    const def = applyNodes(base, row.lockedBy ? [...nodes, row.lockedBy.nodeId] : nodes);
    const fx = a ? abilityInfo(a, def, ctx.content) : { name: row.name, facts: '', cost: '', desc: [], extra: [] };
    infoHead(abilityIconSpec(row.abilityId), fx.name, fx.facts, fx.cost);
    const body = el('div', 'ksk-info-scroll', info.body);
    for (const d of fx.desc.slice(0, 2)) el('p', `ksk-desc${d.length > 220 ? ' long' : ''}`, body, d);
    if (row.lockedBy) el('p', 'ksk-lockline', body, `Learn ${row.lockedBy.name} to unlock.`);
    if (fx.extra.length) {
      divider(body);
      el('h3', 'ksk-h', body, 'Procs');
      for (const x of fx.extra) el('p', 'ksk-proc', body, x);
    }
    const touching = talentsByAbility(base).get(row.abilityId) ?? [];
    const learned = touching.filter((t) => nodes.includes(t.nodeId));
    if (touching.length) {
      divider(body);
      el('h3', 'ksk-h', body, 'Talent');
      for (const t of learned.slice(0, 2)) el('p', 'ksk-proc', body, t.change);
      const icons = el('div', 'ksk-talent-icons', body);
      for (const t of touching.slice(0, 5)) {
        const on = nodes.includes(t.nodeId);
        roundIcon(talentIconSpec(t.nodeId), {
          size: 'md',
          ring: on ? 'gold' : 'steel',
          dim: !on,
          title: `${t.name}${on ? '' : ' (not learned)'}`,
          onClick: () => { tab = 'talents'; selNode = t.nodeId; render(); },
        }, icons);
      }
    }
  }

  // ---- Talents -------------------------------------------------------------

  function renderTalents(base: ClassDef, nodes: TalentId[], grid: ReturnType<typeof talentGrid>): void {
    const groups = talentGroups(grid);
    const all = groups.flatMap((g) => g.rows);
    if (!all.some((r) => r.view.node.id === selNode)) selNode = (all.find((r) => r.view.state === 'available') ?? all[0])?.view.node.id ?? null;

    const head = el('div', 'ksk-points', listPanel.body);
    el('span', 'ksk-points-label', head, 'Points');
    el('span', `ksk-points-n km-num${grid.remaining > 0 ? ' has' : ''}`, head, String(Math.max(0, grid.remaining)));
    el('span', 'ksk-points-total km-num', head, `/ ${grid.points}`);

    const scroll = el('div', 'ksk-scroll', listPanel.body);
    for (const g of groups) {
      listGroupHeader(g.label, g.lockText, scroll);
      for (const r of g.rows) {
        const v = r.view;
        const taken = v.state === 'taken';
        skillRow({
          icon: talentIconSpec(v.node.id),
          name: v.node.name,
          sub: r.lockText || (r.capstone ? `Capstone · ${r.costText}` : r.costText),
          subTone: r.lockText ? 'muted' : r.capstone ? 'gold' : 'blue',
          tag: taken ? 'Learned' : undefined,
          goldRing: taken,
          dimIcon: !taken && v.state !== 'available',
          locked: v.state === 'locked' && v.reasons.some((x) => x.kind === 'rank' || x.kind === 'tier' || x.kind === 'capstone'),
          selected: v.node.id === selNode,
          onClick: () => { selNode = v.node.id; render(); },
        }, scroll);
      }
    }
    scrollSelectedIntoView(scroll);

    secondaryButton('Reset', {
      size: 'md',
      disabled: grid.spent === 0,
      title: 'Unlearn every talent (free)',
      onClick: () => apply(resetTalents(store.profile, classId()), 'Talents reset', () => store.act('talentReset', { classId: classId() })),
    }, actions);
    secondaryButton('Recommended', {
      size: 'md',
      title: 'Learn the recommended build for your points and rank',
      onClick: () => apply(applyRecommended(store.profile, classId()), 'Recommended build learned', () => store.act('talentRecommended', { classId: classId() })),
    }, actions);

    // Ring: capstone (or top pick) in the centre, other picks around; class emblem when empty.
    const rp = ringPicks(nodes);
    skillRing(
      rp.center ? talentIconSpec(rp.center) : classEmblemSvg(classId()),
      rp.around.map((id) => ({ icon: talentIconSpec(id), title: talentNode(id)?.name ?? id, active: true, onClick: () => { selNode = id; render(); } })),
      { onCenter: rp.center ? () => { selNode = rp.center; render(); } : undefined },
      ringHost,
    );

    if (selNode) talentPanel(base, nodes, grid, selNode);
  }

  function talentPanel(base: ClassDef, nodes: TalentId[], grid: ReturnType<typeof talentGrid>, id: TalentId): void {
    const n = talentNode(id);
    if (!n) return;
    const v = nodeView(classId(), nodes, id, grid.points, grid.maxTier);
    const tierName = ['', 'Tier I', 'Tier II', 'Tier III'][n.tier] ?? `Tier ${n.tier}`;
    infoHead(talentIconSpec(id), n.name, `${tierName}${isCapstone(n) ? ' · Capstone' : ''}`, `${n.cost} pt${n.cost === 1 ? '' : 's'}`);
    const body = el('div', 'ksk-info-scroll', info.body);
    el('p', 'ksk-desc', body, n.description);
    let groups: ReturnType<typeof describeNode> = [];
    try {
      groups = describeNode(n, base, (aid) => ctx.content.ability(aid)?.name ?? aid);
    } catch {
      groups = [];
    }
    if (groups.length) {
      divider(body);
      el('h3', 'ksk-h', body, 'Changes');
      for (const g of groups.slice(0, 3)) {
        const row = el('div', 'ksk-change', body);
        if (g.ability) el('div', 'ksk-change-head', row, `${g.key ? `[${g.key}] ` : ''}${g.ability}`);
        for (const c of g.changes.slice(0, 2)) el('p', 'ksk-proc', row, c);
      }
    }
    const foot = el('div', 'ksk-info-foot', info.body);
    if (v.state === 'taken') {
      el('div', 'ksk-state good', foot, 'Learned');
      secondaryButton('Unlearn', { size: 'md', block: true, onClick: () => apply(unlearnNode(store.profile, classId(), id), `${n.name} unlearned`, () => store.act('talentUnlearn', { classId: classId(), id })) }, foot);
    } else {
      const blocking = v.reasons.filter((r) => r.kind !== 'choice');
      if (blocking.length) el('div', 'ksk-state bad', foot, lockReasonText(blocking[0]!));
      else if (v.state === 'excluded') el('div', 'ksk-state', foot, lockReasonText(v.reasons[0]!).replace(/ \(unlearn it to pick this\)/, ''));
      primaryButton(v.state === 'excluded' ? 'Swap' : 'Learn', {
        size: 'md',
        block: true,
        sub: `${n.cost} pt${n.cost === 1 ? '' : 's'}`,
        disabled: blocking.length > 0,
        onClick: () => apply(learnNode(store.profile, classId(), id), `${n.name} learned`, () => store.act('talentLearn', { classId: classId(), id })),
      }, foot);
    }
  }

  // ---- shared ----------------------------------------------------------------

  function infoHead(icon: IconSpec, name: string, facts: string, cost: string): void {
    const h = el('div', 'ksk-info-head', info.body);
    roundIcon(icon, { size: 'xl', ring: 'gold' }, h);
    const t = el('div', 'ksk-info-titles', h);
    el('div', 'ksk-info-name', t, name);
    const line = el('div', 'ksk-info-facts', t);
    if (facts) el('span', '', line, facts);
    if (facts && cost) el('span', '', line, ' · ');
    if (cost) el('span', 'ksk-blue', line, cost);
  }

  function scrollSelectedIntoView(scroll: HTMLElement): void {
    scroll.scrollTop = keepScroll;
    requestAnimationFrame(() => {
      const s = scroll.querySelector<HTMLElement>('.km-xrow.selected');
      if (!s) return;
      const top = s.offsetTop - scroll.offsetTop;
      if (top < scroll.scrollTop || top + s.offsetHeight > scroll.scrollTop + scroll.clientHeight) scroll.scrollTop = Math.max(0, top - scroll.clientHeight / 3);
    });
  }

  render();
  const unsub = store.subscribe(() => render());
  return {
    el: f.el,
    dispose() {
      unsub();
      stage?.dispose();
      stage = null;
    },
  };
};
